import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box, Button, Form, Text } from '@factorialco/deprecated-design-system'
import { RateConfigurationsDatatableDocument } from 'generated/resources/datatable.graphql'
import {
  EmployeeForSelectFragment,
  RateConfigurationAssigneesDataDocument,
  RateConfigurationFormFragment,
  UpdateRateConfigurationDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import {
  ExpensableRateConfigurationResourceType,
  ExpensableRateConfigurationResourceTypeEnum,
} from 'modules/expenses/types/Expensable'

interface FormValues {
  employees: EmployeeForSelectFragment[] | undefined
}

interface Props {
  title: string
  description: string
  rateConfiguration: RateConfigurationFormFragment
  resourceType: ExpensableRateConfigurationResourceType
  onClose: () => void
}

const AssigneesModal = ({
  title,
  description,
  rateConfiguration,
  onClose,
  resourceType,
}: Props) => {
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [updateRateConfiguration] = useMutation(UpdateRateConfigurationDocument, {
    extractorFn: (data) => data.expensesMutations.updateExpensesRateConfiguration,
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache',
    refetchQueries: [RateConfigurationsDatatableDocument],
  })

  const { loading: loadingEmployeesData, data: employeesData } = useQuery(
    RateConfigurationAssigneesDataDocument,
    {
      variables: {
        accessIds: rateConfiguration?.accessIds ?? [],
      },
    }
  )

  const form = useForm<FormValues>({
    validateOnChange: false,
    defaultValues: {
      employees: [],
    },
    validate: (data) => {
      const errors: FormErrors<FormValues> = {}

      if (data.employees?.length === 0) {
        errors.employees = i18n.t(
          'expenses_v2.settings.rate_configurations.detail.assignees_modal.errors.assignees_empty'
        )
      }
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const variables = {
        name: rateConfiguration.name,
        currency: rateConfiguration.currency,
        companyId: company.id,
        legalEntityIds: rateConfiguration.legalEntityIds,
        strategy: rateConfiguration.strategy,
        amountCents: rateConfiguration.amountCents,
        rate: rateConfiguration.rate,
        accessIds:
          data.employees && data.employees?.length > 0
            ? data?.employees?.map((x) => x.access.id)
            : [],
      }

      const result = await updateRateConfiguration({
        variables: { ...variables, id: rateConfiguration.id },
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })
      if (!handled.success) return handled
      handleSuccess(handled.success)
      return handled
    },
  })

  useEffect(() => {
    if (!loadingEmployeesData && (rateConfiguration?.accessIds ?? []).length > 0) {
      form.setFieldValue('employees', employeesData?.employees.employeeNamesConnection.nodes ?? [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingEmployeesData, !!employeesData, !!rateConfiguration])

  const handleSuccess = (success: boolean) => {
    if (success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })

      const redirectPath =
        resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
          ? navigation.settings.expenses.rateConfigurations.fullPath()
          : navigation.settings.expenses.mileageRates.fullPath()

      navigate(redirectPath)
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
  }

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.close')}
          </Button>
          <Button onClick={() => form.handleSubmit()} disabled={form.isSubmitting}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Form padding="s0">
        <EmployeeSelector
          multiple
          alwaysOpen
          usingAccessIds
          onSelect={(selectedEmployees) => {
            form.setFieldValue('employees', selectedEmployees)
          }}
          value={form.values.employees ?? []}
          error={form.errors.employees}
        />
        {form.errors.employees && (
          <Box alignItems="flexStart">
            <Text.Normal size="200" color="radicalRed1000" capitalize>
              {form.errors.employees}
            </Text.Normal>
          </Box>
        )}
      </Form>
    </Modal>
  )
}

export default AssigneesModal
